import { isPlatformBrowser } from '@angular/common';
import { Component, HostListener, Inject, OnDestroy, OnInit, PLATFORM_ID, ViewChild } from '@angular/core';
import { AbstractControl, FormArray, FormBuilder, FormGroup, ValidationErrors, Validators } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { MessageService } from 'primeng/api';
import { InputSwitch } from 'primeng/inputswitch';
import { catchError, debounceTime, forkJoin, Observable, of, Subject, Subscription, switchMap, takeUntil, throwError } from 'rxjs';
import { DiasSemana } from '../../../enums/DiaSemana.enum';
import { TiposDeCoberturaDeEnvio } from '../../../enums/TiposDeCoberturaDeEnvio.enum';
import { environment } from '../../../environments/environment';
import {
  EmpresaConfigDTO,
  EmpresaConfigPrecoCoberturaDeEnvioDTO,
  EmpresaConfigResumidoDTO,
  ItemDeFreteResumidoDTO,
} from '../../../models/configuracoes/empresa.model';
import { HoraDeAtendimentoDTO, HoraDeAtendimentoResponseDTO } from '../../../models/hora-de-atendimento.models';
import { EmpresaConfigService } from '../../../services/configuracoes/empresa.service';
import { DiaDeAtendimentoService } from '../../../services/dia-de-atendimento/dia-de-atendimento.service';
import { DistanciaDeEntregaService } from '../../../services/distancia-de-entrega/distancia-de-entrega.service';
import { HoraDeAtendimentoService } from '../../../services/hora-de-atendimento/hora-de-atendimento.service';
import { NotificacaoBrowserService } from '../../../services/notificacao/notificacao-browser.service';
import { ItemsService } from '../../../services/financeiro/item.service';
import { ImagemUtils } from '../../../shared/utils/imagem-utils';
import { corrigirIconesLeaflet } from '../../../shared/utils/leaflet-icons.util';

@Component({
  selector: 'app-configuracao-mine-shop',
  templateUrl: './configuracao-mine-shop.component.html',
  styleUrl: './configuracao-mine-shop.component.scss'
})
export class ConfiguracaoMineShopComponent implements OnInit, OnDestroy {
  env = environment;

  // Controle de Visualização (Modais)
  mostrarCadastrarHorarios = false;
  mostrarConfigurarAparencia = false;
  mostrarConfigurarPrecoECobertura = false;

  // Formulários
  formHorario: FormGroup;
  formAparencia: FormGroup;
  formCoberturaEnvio: FormGroup;

  // Listas e DTOs
  horariosExistentes: HoraDeAtendimentoDTO[] = [];
  horariosPorDia: { [key: string]: HoraDeAtendimentoDTO[] } = {};
  estadoInicialDias: Record<string, boolean> = {};
  configuracaoEmpresaAtual?: EmpresaConfigDTO;
  faixasOriginais: any[] = [];

  // Estado do switch de habilitar/desabilitar Mini Shopping
  habilitarMiniShopping = false;
  /**
   * Chave "Habilitar MiniShop". O InputSwitch muda o proprio valor no clique; como o modelo
   * (`habilitarMiniShopping`) so muda apos o backend confirmar, ao cancelar o dialogo ou falhar a
   * chamada o ngModel nao reescreve o componente — por isso o valor e devolvido via writeValue.
   */
  @ViewChild('chaveHabilitar') chaveHabilitar?: InputSwitch;

  /**
   * Formas de pagamento exibidas no checkout do minishop.
   * As MESMAS regras valem para a tela interna de Criar Pedido.
   */
  formasDePagamento = {
    exibir: false,
    dinheiro: true,
    tarjeta: false,
    qrCode: false,
    transferencia: false,
  };

  /**
   * O que o pedido de consumo no local exige. Duas opções independentes: restaurante de mesa usa
   * a mesa, balcão usa a comanda, e há quem use as duas.
   *
   * Valem para o checkout do minishop E para a tela interna de Criar Pedido.
   */
  exigenciasDoPedido = {
    numeroDaMesa: false,
    numeroDaComanda: false,
  };
  salvandoExigenciasDoPedido = false;

  /** A empresa configurou os dados de recebimento do QR Vendas (Bancard). */
  bancardConfigurado = false;

  /**
   * A empresa configurou as chaves do tpago — a API do link de pagamento por cartão.
   * São dados diferentes dos do QR: ter um configurado não habilita o outro.
   */
  tpagoConfigurado = false;

  salvandoFormasDePagamento = false;
  loadingHabilitar = false;

  // Preferencia local: tocar som ao receber novo pedido (persistida no localStorage)
  somNotificacaoHabilitado = true;

  // Diálogo para solicitar o Nome da loja ao habilitar o Mini Shopping
  // sem URL configurada. O usuário digita o NOME; internamente o
  // frontend converte para um slug (formato esperado pelo backend em
  // urlDoMiniShopping). Contrato com backend preservado.
  mostrarDialogUrl = false;
  /** Nome da loja digitado pelo usuário (ex: "Mi Tienda"). */
  nomeLojaParaHabilitar = '';
  salvandoUrl = false;

  /** Tamanho mínimo do slug derivado (após normalização). */
  readonly slugMinChars = 3;
  /** Tamanho máximo do slug derivado (após normalização). */
  readonly slugMaxChars = 60;

  // Arrays de Cobertura
  faixasKM: FormArray = this.fb.array([]);
  faixasMetros: FormArray = this.fb.array([]);

  // Upload / preview
  imageBase64: string | null = null;
  imagePreviewUrl: string | null = null;
  fileName: string | null = null;

  diasSemanaOptions: { value: string; label: string }[] = [];
  tiposDeCoberturaDeEnvioOptions: { value: string; label: string }[] = [];

  // Variáveis do Mapa (Leaflet)
  private map: any;
  private L: any;
  private circleGroup: any;
  private center: [number, number] = [-25.776104, -55.074783];

  // Outros estados
  isEditandoEndereco = false;
  enderecoTemporario: string | undefined = '';

  // Modal de edição de endereço com mapa
  mostrarModalEndereco = false;
  enderecoRua = '';
  enderecoNumero = '';
  enderecoComplemento = '';
  enderecoReferencia = '';
  carregandoLocalizacao = false;
  erroGeolocalizacao = '';
  private mapEndereco: any = null;
  private markerEndereco: any = null;
  private geocodingTimeout: any = null;
  mostrarConfirmarResetCores = false;
  mostrarConfirmarResetHorarios = false;

  // Estado de autosave dos horários
  estadoSalvamentoHorarios: 'salvando' | 'guardado' | 'erro' | null = null;
  // Detalhe do último erro do autosave de horários (exibido como tooltip no badge).
  detalheErroAutosaveHorarios: string = '';
  private autosaveHorariosSub: Subscription | null = null;
  private destroy$ = new Subject<void>();
  private salvandoHorariosAgora = false;
  private autosaveHorariosPendente = false;

  // Estado de autosave da aparência
  estadoSalvamentoAparencia: 'salvando' | 'guardado' | 'erro' | null = null;
  private autosaveAparenciaSub: Subscription | null = null;
  private destroyAparencia$ = new Subject<void>();

  // Estado de autosave de cobertura
  estadoSalvamentoCobertura: 'salvando' | 'guardado' | 'erro' | null = null;
  private autosaveCoberturaTimers: any[] = [];
  private destroyCobertura$ = new Subject<void>();
  // Flag para suprimir autosaves secundários durante troca de tipo de cobertura
  private autosaveCoberturaSuprimido = false;
  // Flag para evitar empilhamento de toasts de erro de autosave de cobertura
  private toastErroCoberturaAtivo = false;

  // --- Item de frete (Mini Shop) ---
  /** Sugestoes carregadas no p-autoComplete durante a busca textual. */
  sugestoesItemDeFrete: ItemDeFreteResumidoDTO[] = [];
  /** Subject para debounce da busca do autocomplete (>= 300ms). */
  private buscaItemFrete$ = new Subject<string>();
  /** Flag que indica que o usuario alterou o tipo de cobertura na sessao atual.
   *  Usada para incluir `itemDeFreteId` no payload MERGE mesmo que o controle
   *  nao esteja dirty (ver D6 do DESIGN_FE). */
  private tipoMudouNestaSessao = false;

  coresFaixas: string[] = ['#FF1900', '#FFA809', '#FFFF00', '#008000', '#0000FF'];

  // Valores padrão (serão atualizados ao carregar a empresa)
  moedaSimbolo: string = 'BRL';
  moedaLocal: string = 'pt-BR';

  get casasDecimaisDaMoeda(): number {
    return this.configuracaoEmpresaAtual?.moeda?.casasDecimais ?? 0;
  }

  private mainMarker: any;

  constructor(
    private fb: FormBuilder,
    private translate: TranslateService,
    private horaDeAtendimentoService: HoraDeAtendimentoService,
    private diaDeAtendimentoService: DiaDeAtendimentoService,
    private empresaConfigService: EmpresaConfigService,
    private distanciaDeEntregaService: DistanciaDeEntregaService,
    private messageService: MessageService,
    private notificacaoBrowser: NotificacaoBrowserService,
    private itemsService: ItemsService,
    @Inject(PLATFORM_ID) private platformId: Object
  ) {
    this.buildDiasSemanaOptions();
    this.buildTiposDeCoberturaDeEnvioOptions();
    this.formHorario = this.initFormHorario();
    this.formAparencia = this.initFormAparencia();
    this.formCoberturaEnvio = this.initFormCobertura();
    this.somNotificacaoHabilitado = this.notificacaoBrowser.somHabilitado;
  }

  onToggleSomNotificacao(habilitado: boolean): void {
    this.notificacaoBrowser.somHabilitado = habilitado;
    this.somNotificacaoHabilitado = habilitado;
    const chave = habilitado
      ? 'configuracao-mine-shop.texts.notificacao_sonora_ativada'
      : 'configuracao-mine-shop.texts.notificacao_sonora_desativada';
    this.messageService.add({
      severity: habilitado ? 'success' : 'info',
      summary: this.translate.instant(chave),
      life: 2500,
    });
  }

  private langChangeSub!: Subscription;

  ngOnInit(): void {
    this.buildDiasSemanaOptions();
    this.findHoraDeAtendimento();
    this.carregarConfiguracaoEmpresa();
    this.registrarBuscaItemDeFrete();

    this.langChangeSub = this.translate.onLangChange.subscribe(() => {
      this.buildDiasSemanaOptions();
      this.buildTiposDeCoberturaDeEnvioOptions();
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.destroyAparencia$.next();
    this.destroyAparencia$.complete();
    this.destroyCobertura$.next();
    this.destroyCobertura$.complete();
    if (this.autosaveHorariosSub) {
      this.autosaveHorariosSub.unsubscribe();
    }
    if (this.autosaveAparenciaSub) {
      this.autosaveAparenciaSub.unsubscribe();
    }
    this.autosaveCoberturaTimers.forEach(t => clearTimeout(t));
  }

  /* --- Atalhos de Teclado --- */

  @HostListener('document:keydown', ['$event'])
  onGlobalKeydown(ev: KeyboardEvent) {
    // 1. Escape: fechar sidebars (prioridade maxima)
    if (ev.key === 'Escape') {
      if (this.mostrarConfigurarPrecoECobertura) {
        ev.preventDefault();
        this.closePrecoECoberturaDialog();
        return;
      }
      if (this.mostrarConfigurarAparencia) {
        ev.preventDefault();
        this.closeAparenciaDialog();
        return;
      }
      if (this.mostrarCadastrarHorarios) {
        ev.preventDefault();
        this.closeDialog();
        return;
      }
      return;
    }

    // 2. Ctrl+Enter: todos os sidebars agora usam autosave, atalho não aplicável

    // 3. Ctrl+N: adicionar item no contexto ativo
    if (ev.ctrlKey && (ev.key === 'n' || ev.key === 'N')) {
      ev.preventDefault();
      if (this.mostrarConfigurarPrecoECobertura) {
        const tipo = this.formCoberturaEnvio.get('tipoPrecoCoberturaDeEnvio')?.value;
        if (tipo === 'DISTANCIA_RAIO_METROS') {
          this.adicionarRango();
        }
        return;
      }
      return;
    }

    // 4. F2/F3/F4: abrir sidebars (somente se nenhum sidebar aberto)
    if (this.nenhumSidebarAberto()) {
      if (ev.key === 'F2') {
        ev.preventDefault();
        this.openDialogHorarios();
        return;
      }
      if (ev.key === 'F3') {
        ev.preventDefault();
        this.openDialogAparencia();
        return;
      }
      if (ev.key === 'F4') {
        ev.preventDefault();
        this.openDialogPrecoECobertura();
        return;
      }
    }
  }

  /** Verifica se nenhum sidebar esta aberto */
  private nenhumSidebarAberto(): boolean {
    return !this.mostrarCadastrarHorarios
      && !this.mostrarConfigurarAparencia
      && !this.mostrarConfigurarPrecoECobertura;
  }

  /* --- Inicialização de Formulários --- */

  private buildDiasSemanaOptions() {
    this.diasSemanaOptions = Object.values(DiasSemana).map(periodo => ({
      value: periodo,
      label: this.translate.instant(`dias_semana.${periodo}`)
    }));
  }

  private buildTiposDeCoberturaDeEnvioOptions() {
    this.tiposDeCoberturaDeEnvioOptions = Object.values(TiposDeCoberturaDeEnvio).map(tipo => ({
      value: tipo,
      label: this.translate.instant(`tipos_de_cobertura_de_envio.${tipo}`)
    }));
  }

  private initFormHorario(): FormGroup {
    return this.fb.group({
      dias: this.fb.group(
        this.diasSemanaOptions.reduce((acc, dia) => {
          acc[dia.value] = this.fb.group({
            ativo: [false],
            horarios: this.fb.array([])
          });
          return acc;
        }, {} as { [key: string]: FormGroup })
      )
    });
  }

  private initFormAparencia(): FormGroup {
    return this.fb.group({
      minishopGeralCorDeFundo: ['#FFFFFF'],
      minishopGeralCorDaFonte: ['#474747'],
      minishopBotoesCorDeFundo: ['#0582c5'],
      minishopBotoesCorDaFonte: ['#FFFFFF'],
      minishopGruposCorDeFundo: ['#165c7d'],
      minishopGruposCorDaFonte: ['#FFFFFF'],
      minishopCardCorDeFundo: ['#165c7d'],
      minishopCardCorDaFonte: ['#FFFFFF'],
      minishopCabecalhoCorDeFundo: ['#0582c5'],
      minishopCabecalhoCorDaFonte: ['#FFFFFF'],
      minishopImagemDeTitulo: [''],
      perfilInstagram: [''],
      perfilFacebook: [''],
      telefone: [''],
      urlDoMiniShopping: ['']
    });
  }

  private initFormCobertura(): FormGroup {
    return this.fb.group({
      tipoPrecoCoberturaDeEnvio: [null], // Inicializa vazio
      aceitarForaCobertura: [false],
      precoFixo: [0],
      rangos: this.fb.array([]),
      // Controles do autocomplete de Item de Frete (ver DESIGN_FE D2).
      // `itemDeFreteSelecionado` guarda o objeto exibido pelo p-autoComplete.
      // `itemDeFreteId` guarda apenas o id enviado no payload PATCH.
      itemDeFreteSelecionado: [null],
      itemDeFreteId: [null],
    });
  }

  private reinitFormCobertura(): void {
    this.formCoberturaEnvio = this.fb.group({
      tipoPrecoCoberturaDeEnvio: ['SEM_PRECO'],
      precoFixo: [0],
      rangos: this.faixasMetros,
      itemDeFreteSelecionado: [null],
      itemDeFreteId: [null],
    });
  }

  /** Getter auxiliar: tipo de cobertura atualmente selecionado no form (string | null). */
  get tipoSelecionadoCobertura(): string | null {
    return this.formCoberturaEnvio?.get('tipoPrecoCoberturaDeEnvio')?.value ?? null;
  }

  /**
   * Getter para exibicao do produto de frete como "#ID - Nome".
   * O campo eh gerenciado pelo sistema (travado), entao so exibimos o valor.
   */
  get itemDeFreteDisplay(): string {
    const item = this.formCoberturaEnvio?.get('itemDeFreteSelecionado')?.value as
      | ItemDeFreteResumidoDTO
      | null;
    if (!item) return '';
    return `#${item.id} - ${item.nome}`;
  }

  /**
   * Aplica ou remove Validators.required no controle `itemDeFreteId` conforme o tipo.
   * Quando o tipo exige cobranca de frete (!= SEM_PRECO), o item passa a ser obrigatorio.
   */
  private aplicarValidadorItemDeFrete(tipo: string | null): void {
    const ctrl = this.formCoberturaEnvio.get('itemDeFreteId');
    if (!ctrl) return;

    if (tipo && tipo !== 'SEM_PRECO') {
      ctrl.setValidators([Validators.required]);
    } else {
      ctrl.clearValidators();
    }
    ctrl.updateValueAndValidity({ emitEvent: false });
  }

  /**
   * Registra o pipeline reativo da busca textual no autocomplete de Item de Frete.
   * Aplica debounceTime(300) + distinctUntilChanged + switchMap (cancela requisicoes obsoletas).
   */
  private registrarBuscaItemDeFrete(): void {
    this.buscaItemFrete$
      .pipe(
        debounceTime(300),
        switchMap((query) =>
          // ApiQueryParamsDTO nao conhece `tipoItem`/`status`/`nome` — usamos `any`
          // para nao alterar o tipo global consumido por varias telas.
          this.itemsService.get('/item', {
            tipoItem: 'SERVICO',
            status: true,
            nome: query,
            size: 10,
            page: 0,
            sort: 'nome,asc',
          } as any).pipe(
            catchError(() => of({ content: [] } as any)),
          ),
        ),
        takeUntil(this.destroy$),
      )
      .subscribe((resp: any) => {
        this.sugestoesItemDeFrete = (resp?.content ?? []) as ItemDeFreteResumidoDTO[];
      });
  }

  /** Callback do p-autoComplete: alimenta o Subject de busca. */
  onBuscarItemDeFrete(event: { query: string }): void {
    this.buscaItemFrete$.next(event?.query ?? '');
  }

  /** Callback de selecao de um item nas sugestoes. Sincroniza id + objeto e dispara autosave.
   *  O evento do p-autoComplete (PrimeNG 17) vem no formato `{ originalEvent, value }`. */
  onSelecionarItemDeFrete(event: any): void {
    const item: ItemDeFreteResumidoDTO | null = event?.value ?? event ?? null;
    if (!item) return;
    this.formCoberturaEnvio.patchValue({
      itemDeFreteSelecionado: item,
      itemDeFreteId: item.id,
    });
    const ctrl = this.formCoberturaEnvio.get('itemDeFreteId');
    ctrl?.markAsDirty();
    ctrl?.updateValueAndValidity();
    this.executarAutosaveCobertura();
  }

  /** Callback de limpeza do autocomplete. Marca dirty mas NAO dispara autosave
   *  (a validacao condicional bloqueara o autosave se o tipo exigir frete). */
  onLimparItemDeFrete(): void {
    this.formCoberturaEnvio.patchValue({
      itemDeFreteSelecionado: null,
      itemDeFreteId: null,
    });
    const ctrl = this.formCoberturaEnvio.get('itemDeFreteId');
    ctrl?.markAsDirty();
    ctrl?.markAsTouched();
    ctrl?.updateValueAndValidity();
  }

  /** Extrai o codigo de erro 422 retornado pelo backend (pode vir como `code` ou `error`). */
  private extrairCodigoErroBackend(err: any): string | null {
    return err?.error?.code ?? err?.error?.error ?? null;
  }

  /* --- Seção: Horários de Atendimento --- */

  findHoraDeAtendimento(page: number = 0, sort: string = 'id,asc'): void {
    this.horaDeAtendimentoService
      .get('/horaDeAtendimento', { page, size: 50, sort })
      .subscribe((response: HoraDeAtendimentoResponseDTO) => {
        this.horariosExistentes = response.content;
        this.horariosPorDia = this.agruparHorariosPorDia(this.horariosExistentes);
      });
  }

  private agruparHorariosPorDia(horarios: HoraDeAtendimentoDTO[]) {
    return horarios.reduce((acc, h) => {
      const dia = h.diaDeAtendimento.diaDaSemana;
      if (!acc[dia]) acc[dia] = [];
      acc[dia].push(h);
      acc[dia].sort((a, b) => a.horaInicial.localeCompare(b.horaInicial));
      return acc;
    }, {} as { [key: string]: HoraDeAtendimentoDTO[] });
  }

  openDialogHorarios(): void {
    this.preencherFormularioComHorariosExistentes();
    this.estadoInicialDias = {};
    this.diasSemanaOptions.forEach(dia => {
      const enumDia = this.mapDiaToEnum(dia.value);
      this.estadoInicialDias[dia.value] = !!this.horariosPorDia[enumDia]?.length;
    });
    this.estadoSalvamentoHorarios = null;
    this.mostrarCadastrarHorarios = true;
    this.registrarAutosaveHorarios();
  }

  closeDialog(): void {
    this.mostrarCadastrarHorarios = false;
    this.estadoSalvamentoHorarios = null;
    if (this.autosaveHorariosSub) {
      this.autosaveHorariosSub.unsubscribe();
      this.autosaveHorariosSub = null;
    }
  }

  getHorarios(dia: string): FormArray {
    return this.formHorario.get(`dias.${dia}.horarios`) as FormArray;
  }

  /**
   * Retorna true quando o campo (abertura ou fechamento) do horário indicado está
   * incompleto — ou seja, o usuário digitou algo mas não preencheu todos os dígitos
   * da máscara. Usado no template para exibir erro visual.
   */
  temHoraIncompleta(dia: string, index: number, campo: 'abertura' | 'fechamento'): boolean {
    const ctrl = (this.getHorarios(dia).at(index) as FormGroup).get(campo);
    return !!(ctrl?.errors?.['horaIncompleta'] && ctrl?.dirty);
  }

  /**
   * Zera os IDs dos controles de horário de um dia no FormArray.
   * Usado após DELETE bem-sucedido (desativação do dia), para que um novo
   * toggle-on subsequente gere POST em vez de PUT em IDs já removidos — o
   * que causaria "Nenhuma hora de atendimento encontrada" no backend.
   * Usa emitEvent:false para não disparar autosave em cascata.
   */
  private limparIdsDosHorariosDoDia(dia: string): void {
    const horariosArray = this.getHorarios(dia);
    horariosArray.controls.forEach(ctrl => {
      ctrl.get('id')?.setValue(null, { emitEvent: false });
    });
  }

  adicionarHorario(dia: string): void {
    this.getHorarios(dia).push(this.criarHorarioComId(null, '08:00', '18:00'));
    // Autosave imediato ao adicionar período
    this.executarAutosaveHorarios();
  }

  /**
   * Toggle de ativo/inativo de um dia:
   * - Ao ativar sem períodos, pré-popula os 2 turnos padrão (07:00-12:00 e
   *   13:30-18:00) com emitEvent:false para não gerar múltiplos valueChanges.
   * - Sempre dispara o autosave ao final, garantindo que a persistência
   *   ocorra após a população dos horários (evitando salvar array vazio).
   */
  onToggleDiaAtivo(dia: string, ativo: boolean): void {
    if (ativo) {
      const horariosArray = this.getHorarios(dia);
      if (horariosArray.length === 0) {
        horariosArray.push(this.criarHorarioComId(null, '07:00', '12:00'), { emitEvent: false });
        horariosArray.push(this.criarHorarioComId(null, '13:30', '18:00'), { emitEvent: false });
      }
    }
    this.executarAutosaveHorarios();
  }

  removerHorario(dia: string, index: number): void {
    const array = this.getHorarios(dia);
    const id = array.at(index).get('id')?.value;
    if (id) {
      this.horaDeAtendimentoService.delete(`/horaDeAtendimento/${id}`).subscribe({
        next: () => {
          array.removeAt(index);
          // Autosave para persistir o estado atualizado do dia
          this.executarAutosaveHorarios();
        },
        error: (err) => {
          const detalheErro = err?.error?.message || err?.message || JSON.stringify(err?.status);
          this.messageService.add({
            severity: 'error',
            summary: this.translate.instant('common.erro'),
            detail: `${this.translate.instant('configuracao-mine-shop.texts.erro_salvar_horarios')} [${detalheErro}]`,
          });
        }
      });
    } else {
      array.removeAt(index);
      // Autosave imediato ao remover período sem ID (não persistido)
      this.executarAutosaveHorarios();
    }
  }

  private preencherFormularioComHorariosExistentes(): void {
    const diasForm = this.formHorario.get('dias') as FormGroup;
    this.diasSemanaOptions.forEach(dia => {
      const enumDia = this.mapDiaToEnum(dia.value);
      const grupoDia = diasForm.get(dia.value) as FormGroup;
      const array = grupoDia.get('horarios') as FormArray;

      array.clear();
      const horarios = this.horariosPorDia[enumDia] || [];

      if (horarios.length === 0) {
        grupoDia.get('ativo')?.setValue(false);
        return;
      }

      grupoDia.get('ativo')?.setValue(true);
      horarios.forEach(h => {
        array.push(this.criarHorarioComId(h.id!, this.formatHora(h.horaInicial), this.formatHora(h.horaFinal)));
      });
    });
  }

  onSubmitHorario(): void {
    // Abortar submit se houver campos de hora incompletos
    if (this.formHorario.invalid) {
      this.formHorario.markAllAsTouched();
      return;
    }

    const diasForm = this.formHorario.get('dias') as FormGroup;
    const operacoes: Observable<any>[] = [];

    this.diasSemanaOptions.forEach(dia => {
      const grupoDia = diasForm.get(dia.value) as FormGroup;
      const ativoAgora = grupoDia.get('ativo')?.value;
      const diaEnum = this.mapDiaToEnum(dia.value);
      const horariosExistentes = this.horariosPorDia[diaEnum] || [];

      // Condição de delete baseada na presença real no banco (horariosPorDia),
      // e não em estadoInicialDias — que pode estar desatualizado enquanto o
      // GET de findHoraDeAtendimento ainda não retornou entre um save e outro.
      if (horariosExistentes.length > 0 && !ativoAgora) {
        // Deletar em sequência: primeiro as horas, depois o dia.
        // Em paralelo (forkJoin) o backend pode deletar o dia antes das
        // horas, violando a FK horadeatendimento → diadeatendimento.
        // Tolera 404: se o registro já foi removido, consideramos sucesso.
        const horaOps = horariosExistentes
          .filter(h => h.id)
          .map(h => this.horaDeAtendimentoService
            .delete(`/horaDeAtendimento/${h.id}`)
            .pipe(catchError(err => err?.status === 404 ? of(null) : throwError(() => err))));
        const diaId = horariosExistentes[0]?.diaDeAtendimento?.id;

        const desativarDia = new Observable(observer => {
          const deletarDia = () => {
            if (!diaId) {
              observer.next(null);
              observer.complete();
              return;
            }
            this.diaDeAtendimentoService.delete(`/diaDeAtendimento/${diaId}`)
              .pipe(catchError(err => err?.status === 404 ? of(null) : throwError(() => err)))
              .subscribe({
                next: () => { observer.next(null); observer.complete(); },
                error: (err) => observer.error(err),
              });
          };

          if (horaOps.length === 0) {
            this.limparIdsDosHorariosDoDia(dia.value);
            deletarDia();
          } else {
            forkJoin(horaOps).subscribe({
              next: () => {
                this.limparIdsDosHorariosDoDia(dia.value);
                deletarDia();
              },
              error: (err) => observer.error(err),
            });
          }
        });
        operacoes.push(desativarDia);
        return;
      }

      if (!ativoAgora) return;

      const horariosArray = grupoDia.get('horarios') as FormArray;
      const horariosForm = horariosArray.value;
      const salvarHorariosDoDia = new Observable(observer => {
        this.diaDeAtendimentoService.criar(diaEnum).subscribe(diaCriado => {
          const horarioOps: Observable<any>[] = [];
          const indicesPost: number[] = [];
          horariosForm.forEach((h: any, idx: number) => {
            const payload: HoraDeAtendimentoDTO = {
              horaInicial: this.dateToLocalTime(h.abertura),
              horaFinal: this.dateToLocalTime(h.fechamento),
              diaDeAtendimento: { id: diaCriado.id!, diaDaSemana: diaEnum }
            };
            // Só faz PUT se o ID do form realmente existir no último GET.
            // Caso contrário, o registro foi removido no backend → cai para POST
            // para evitar "Nenhuma hora de atendimento encontrada" em IDs órfãos.
            const idAindaExiste = h.id != null && horariosExistentes.some(e => e.id === h.id);
            if (idAindaExiste) {
              horarioOps.push(this.horaDeAtendimentoService.put(`/horaDeAtendimento/${h.id}`, payload));
            } else {
              if (h.id != null) {
                const grupo = horariosArray.at(idx) as FormGroup;
                grupo.get('id')?.setValue(null, { emitEvent: false });
              }
              horarioOps.push(this.horaDeAtendimentoService.post('/horaDeAtendimento', payload));
              indicesPost.push(idx);
            }
          });

          if (horarioOps.length === 0) {
            observer.next(null);
            observer.complete();
          } else {
            forkJoin(horarioOps).subscribe({
              next: (resultado: any[]) => {
                indicesPost.forEach(idx => {
                  const response = resultado[idx];
                  const novoId = response?.id ?? response?.body?.id;
                  if (novoId != null) {
                    const grupo = horariosArray.at(idx) as FormGroup;
                    grupo.get('id')?.setValue(novoId, { emitEvent: false });
                  }
                });
                observer.next(resultado);
                observer.complete();
              },
              error: (err) => { observer.error(err); }
            });
          }
        });
      });
      operacoes.push(salvarHorariosDoDia);
    });

    if (operacoes.length === 0) {
      this.findHoraDeAtendimento();
      this.closeDialog();
      return;
    }

    forkJoin(operacoes).subscribe({
      next: () => {
        this.messageService.add({
          severity: 'success',
          summary: this.translate.instant('common.sucesso'),
          detail: this.translate.instant('configuracao-mine-shop.texts.horarios_salvos_sucesso'),
        });
        this.findHoraDeAtendimento();
        this.closeDialog();
      },
      error: (err) => {
        const detalheErro = err?.error?.message || err?.message || JSON.stringify(err?.status);
        this.messageService.add({
          severity: 'error',
          summary: this.translate.instant('common.erro'),
          detail: `${this.translate.instant('configuracao-mine-shop.texts.erro_salvar_horarios')} [${detalheErro}]`,
        });
        // Mesmo com erro, recarrega para mostrar o estado atual
        this.findHoraDeAtendimento();
        this.closeDialog();
      }
    });
  }

  /* --- Autosave de Horários --- */

  /**
   * Registra listeners para autosave ao abrir o painel de horários.
   * - Toggles de abertura/fechamento: salva imediatamente.
   * - Horários de entrada/saída: salva com debounce de 600ms.
   */
  private registrarAutosaveHorarios(): void {
    if (this.autosaveHorariosSub) {
      this.autosaveHorariosSub.unsubscribe();
    }

    const diasForm = this.formHorario.get('dias') as FormGroup;
    const autosaveSubject = new Subject<'imediato' | 'debounce'>();

    // Listener por dia: toggle salva imediato, horários com debounce
    this.diasSemanaOptions.forEach(dia => {
      const grupoDia = diasForm.get(dia.value) as FormGroup;

      // Horários → salva com debounce
      // (o save do toggle ativo/inativo é disparado em onToggleDiaAtivo,
      //  garantindo que a pré-população dos 2 turnos padrão aconteca
      //  antes do save — evitando salvar FormArray vazio)
      const horariosArray = grupoDia.get('horarios') as FormArray;
      horariosArray.valueChanges
        .pipe(takeUntil(this.destroy$))
        .subscribe(() => autosaveSubject.next('debounce'));
    });

    // Dispara o autosave de acordo com o tipo de evento
    let debounceTimer: any = null;
    this.autosaveHorariosSub = autosaveSubject.subscribe(tipo => {
      if (tipo === 'imediato') {
        clearTimeout(debounceTimer);
        this.executarAutosaveHorarios();
      } else {
        clearTimeout(debounceTimer);
        debounceTimer = setTimeout(() => this.executarAutosaveHorarios(), 600);
      }
    });
  }

  /**
   * Executa o salvamento dos horários sem fechar o painel.
   * Atualiza o estado visual (estadoSalvamentoHorarios) para feedback discreto.
   *
   * Guardas contra duplicação:
   * - Se já há um save em andamento, marca como "pendente" e não dispara outro.
   *   Quando o save corrente terminar, executa uma nova rodada.
   * - Após POST bem-sucedido, grava o ID retornado no FormGroup correspondente
   *   (com emitEvent:false) para que o próximo save vire PUT em vez de POST.
   */
  executarAutosaveHorarios(): void {
    if (this.salvandoHorariosAgora) {
      this.autosaveHorariosPendente = true;
      return;
    }

    // Abortar save se houver campos de hora incompletos (máscara não preenchida)
    if (this.formHorario.invalid) {
      this.detalheErroAutosaveHorarios = this.descreverDiasComCamposInvalidos();
      this.estadoSalvamentoHorarios = 'erro';
      return;
    }

    this.salvandoHorariosAgora = true;

    const diasForm = this.formHorario.get('dias') as FormGroup;
    const operacoes: Observable<any>[] = [];

    this.diasSemanaOptions.forEach(dia => {
      const grupoDia = diasForm.get(dia.value) as FormGroup;
      const ativoAgora = grupoDia.get('ativo')?.value;
      const diaEnum = this.mapDiaToEnum(dia.value);
      const horariosExistentes = this.horariosPorDia[diaEnum] || [];

      // Condição de delete baseada na presença real no banco (horariosPorDia),
      // e não em estadoInicialDias — que pode estar desatualizado enquanto o
      // GET de findHoraDeAtendimento ainda não retornou entre um save e outro.
      if (horariosExistentes.length > 0 && !ativoAgora) {
        // Deletar em sequência: primeiro as horas, depois o dia.
        // Em paralelo (forkJoin) o backend pode deletar o dia antes das
        // horas, violando a FK horadeatendimento → diadeatendimento.
        // Tolera 404: se o registro já foi removido, consideramos sucesso.
        const horaOps = horariosExistentes
          .filter(h => h.id)
          .map(h => this.horaDeAtendimentoService
            .delete(`/horaDeAtendimento/${h.id}`)
            .pipe(catchError(err => err?.status === 404 ? of(null) : throwError(() => err))));
        const diaId = horariosExistentes[0]?.diaDeAtendimento?.id;

        const desativarDia = new Observable(observer => {
          const deletarDia = () => {
            if (!diaId) {
              observer.next(null);
              observer.complete();
              return;
            }
            this.diaDeAtendimentoService.delete(`/diaDeAtendimento/${diaId}`)
              .pipe(catchError(err => err?.status === 404 ? of(null) : throwError(() => err)))
              .subscribe({
                next: () => { observer.next(null); observer.complete(); },
                error: (err) => observer.error(err),
              });
          };

          if (horaOps.length === 0) {
            // Sem horas a remover (apenas o dia): ainda assim limpa IDs do form
            // para o caso raro de dessincronização entre UI e banco.
            this.limparIdsDosHorariosDoDia(dia.value);
            deletarDia();
          } else {
            forkJoin(horaOps).subscribe({
              next: () => {
                // Horas deletadas com sucesso: zera IDs do FormArray para que,
                // caso o usuário re-habilite o dia, o próximo save seja POST.
                this.limparIdsDosHorariosDoDia(dia.value);
                deletarDia();
              },
              error: (err) => observer.error(err),
            });
          }
        });
        operacoes.push(desativarDia);
        return;
      }

      if (!ativoAgora) return;

      const horariosArray = grupoDia.get('horarios') as FormArray;
      const horariosForm = horariosArray.value;
      if (!horariosForm || horariosForm.length === 0) return;

      const salvarHorariosDoDia = new Observable(observer => {
        this.diaDeAtendimentoService.criar(diaEnum).subscribe({
          next: (diaCriado) => {
            const horarioOps: Observable<any>[] = [];
            const indicesPost: number[] = [];
            horariosForm.forEach((h: any, idx: number) => {
              const payload: HoraDeAtendimentoDTO = {
                horaInicial: this.dateToLocalTime(h.abertura),
                horaFinal: this.dateToLocalTime(h.fechamento),
                diaDeAtendimento: { id: diaCriado.id!, diaDaSemana: diaEnum }
              };
              // Só faz PUT se o ID do form realmente existir no último GET.
              // Caso contrário, o registro foi removido no backend → cai para POST
              // para evitar "Nenhuma hora de atendimento encontrada" em IDs órfãos.
              const idAindaExiste = h.id != null && horariosExistentes.some(e => e.id === h.id);
              if (idAindaExiste) {
                horarioOps.push(this.horaDeAtendimentoService.put(`/horaDeAtendimento/${h.id}`, payload));
              } else {
                if (h.id != null) {
                  const grupo = horariosArray.at(idx) as FormGroup;
                  grupo.get('id')?.setValue(null, { emitEvent: false });
                }
                horarioOps.push(this.horaDeAtendimentoService.post('/horaDeAtendimento', payload));
                indicesPost.push(idx);
              }
            });

            if (horarioOps.length === 0) {
              observer.next(null);
              observer.complete();
            } else {
              forkJoin(horarioOps).subscribe({
                next: (resultado: any[]) => {
                  // Grava IDs retornados pelos POSTs no FormArray (sem disparar valueChanges)
                  indicesPost.forEach(idx => {
                    const response = resultado[idx];
                    const novoId = response?.id ?? response?.body?.id;
                    if (novoId != null) {
                      const grupo = horariosArray.at(idx) as FormGroup;
                      grupo.get('id')?.setValue(novoId, { emitEvent: false });
                    }
                  });
                  observer.next(resultado);
                  observer.complete();
                },
                error: (err) => { observer.error(err); }
              });
            }
          },
          error: (err) => observer.error(err)
        });
      });
      operacoes.push(salvarHorariosDoDia);
    });

    if (operacoes.length === 0) {
      // Nada a salvar — atualiza estado local e indicador
      this.estadoSalvamentoHorarios = 'guardado';
      this.atualizarHorariosAposSave();
      return;
    }

    this.estadoSalvamentoHorarios = 'salvando';

    forkJoin(operacoes).subscribe({
      next: () => {
        this.detalheErroAutosaveHorarios = '';
        this.estadoSalvamentoHorarios = 'guardado';
        this.atualizarHorariosAposSave();
      },
      error: (err) => {
        this.estadoSalvamentoHorarios = 'erro';
        const detalheErro = err?.error?.message || err?.message || `HTTP ${err?.status ?? '?'}`;
        this.detalheErroAutosaveHorarios = detalheErro;
        // Log no console facilita o diagnóstico em produção (request, status e body do erro).
        console.error('[autosave horários] falhou:', err);
        this.messageService.add({
          severity: 'error',
          summary: this.translate.instant('common.erro'),
          detail: `${this.translate.instant('configuracao-mine-shop.texts.erro_salvar_horarios')} [${detalheErro}]`,
        });
        this.atualizarHorariosAposSave();
      }
    });
  }

  /**
   * Constrói uma descrição amigável dos dias/períodos com campos inválidos para
   * exibir como detalhe do erro de autosave. Útil quando o usuário não percebe
   * que algum campo de hora ficou em estado intermediário (máscara incompleta).
   */
  private descreverDiasComCamposInvalidos(): string {
    const prefix = this.translate.instant('configuracao-mine-shop.texts.autosave_erro_horario_invalido');
    const diasForm = this.formHorario.get('dias') as FormGroup;
    if (!diasForm) return prefix;
    const diasInvalidos: string[] = [];
    this.diasSemanaOptions.forEach(dia => {
      const grupoDia = diasForm.get(dia.value) as FormGroup;
      const horariosArray = grupoDia?.get('horarios') as FormArray;
      if (!horariosArray) return;
      const algumInvalido = horariosArray.controls.some(c => c.invalid);
      if (algumInvalido) {
        diasInvalidos.push(this.translate.instant(`dias_semana.${dia.value}`) || dia.label);
      }
    });
    return diasInvalidos.length ? `${prefix}: ${diasInvalidos.join(', ')}` : prefix;
  }

  /**
   * Busca horários do backend e só libera a flag de save após o GET voltar.
   * Assim o próximo autosave (ou o pendente) já enxerga horariosPorDia
   * atualizado — evitando race condition em cliques rápidos de ativar/desativar.
   */
  private atualizarHorariosAposSave(): void {
    this.horaDeAtendimentoService
      .get('/horaDeAtendimento', { page: 0, size: 50, sort: 'id,asc' })
      .subscribe({
        next: (response: HoraDeAtendimentoResponseDTO) => {
          this.horariosExistentes = response.content;
          this.horariosPorDia = this.agruparHorariosPorDia(this.horariosExistentes);
          this.sincronizarEstadoInicialDias();
          this.finalizarAutosaveHorarios();
        },
        error: () => this.finalizarAutosaveHorarios(),
      });
  }

  /**
   * Libera a flag de save corrente. Se houve tentativas de save enquanto
   * este estava em andamento, dispara uma nova rodada para capturar as
   * alterações pendentes.
   */
  private finalizarAutosaveHorarios(): void {
    this.salvandoHorariosAgora = false;
    if (this.autosaveHorariosPendente) {
      this.autosaveHorariosPendente = false;
      // pequeno delay para não encadear chamadas síncronas
      setTimeout(() => this.executarAutosaveHorarios(), 0);
    }
  }

  /**
   * Atualiza o mapa de estado inicial dos dias após um autosave bem-sucedido,
   * para que o próximo autosave calcule corretamente os deltas.
   */
  private sincronizarEstadoInicialDias(): void {
    this.diasSemanaOptions.forEach(dia => {
      const enumDia = this.mapDiaToEnum(dia.value);
      this.estadoInicialDias[dia.value] = !!this.horariosPorDia[enumDia]?.length;
    });
  }

  /* --- Seção: Aparência --- */

  private carregarConfiguracaoEmpresa(): void {
    // Primeiro busca o ID da empresa da sessao do usuario autenticado,
    // depois busca os dados completos por esse ID.
    // Isso garante que usuarios master carreguem sua propria empresa,
    // e nao a primeira da lista geral.
    this.empresaConfigService.getSessao().pipe(
      switchMap((sessao: any) => {
        return this.empresaConfigService.getById(`/configuracaoEmpresa/${sessao.id}`);
      })
    ).subscribe({
        next: (config) => {
          if (!config) return;

          this.configuracaoEmpresaAtual = config;
          this.habilitarMiniShopping = !!config.habilitarMiniShopping;

          this.bancardConfigurado = config.bancardConfigurado === true;
          this.tpagoConfigurado = config.tpagoConfigurado === true;
          this.formasDePagamento = {
            exibir: config.minishopExibirFormasDePagamento === true,
            // Dinheiro tem default true no backend: `!== false` preserva isso quando o
            // campo ainda nao existir na resposta de uma empresa antiga.
            dinheiro: config.minishopAceitaDinheiro !== false,
            tarjeta: config.minishopAceitaTarjeta === true,
            qrCode: config.minishopAceitaQrCode === true,
            transferencia: config.minishopAceitaTransferencia === true,
          };
          this.exigenciasDoPedido = {
            numeroDaMesa: config.minishopExigirNumeroDaMesa === true,
            numeroDaComanda: config.minishopExigirNumeroDaComanda === true,
          };

          // Configuração de Moeda
          if (config.moeda) {
            this.moedaSimbolo = config.moeda.codigoOperacao;
            this.moedaLocal = config.moeda.pais?.local?.replace('_', '-') ?? 'pt-BR';
          }

          // Extração do Enum de Tipo de Cobertura
          // Atenção: null tem typeof 'object' em JS, por isso verificamos explicitamente
          const tipoEnum = (config.tipoPrecoCoberturaDeEnvio != null && typeof config.tipoPrecoCoberturaDeEnvio === 'object')
            ? config.tipoPrecoCoberturaDeEnvio.enum
            : (config.tipoPrecoCoberturaDeEnvio ?? 'SEM_PRECO');

          // Atualiza Formulário de Cobertura (inclui hidratacao do Item de Frete)
          const itemFrete = config.itemDeFrete ?? null;
          this.formCoberturaEnvio.patchValue({
            tipoPrecoCoberturaDeEnvio: tipoEnum,
            itemDeFreteSelecionado: itemFrete,
            itemDeFreteId: itemFrete?.id ?? null,
          }, { emitEvent: false });

          // Aplica validador condicional conforme o tipo hidratado
          this.aplicarValidadorItemDeFrete(tipoEnum);
          // Reseta a flag de "tipo mudou" — estamos em estado hidratado
          this.tipoMudouNestaSessao = false;

          this.carregarFaixasExistentes();

          // Atualiza o Formulário de Aparência
          // Cores: usa o valor do banco se existir, senao mantem o default do form (hex valido)
          this.formAparencia.patchValue({
            minishopGeralCorDeFundo: config.minishopGeralCorDeFundo?.trim() || '#FFFFFF',
            minishopGeralCorDaFonte: config.minishopGeralCorDaFonte?.trim() || '#474747',
            minishopBotoesCorDeFundo: config.minishopBotoesCorDeFundo?.trim() || '#0582c5',
            minishopBotoesCorDaFonte: config.minishopBotoesCorDaFonte?.trim() || '#FFFFFF',
            minishopGruposCorDeFundo: config.minishopGruposCorDeFundo?.trim() || '#165c7d',
            minishopGruposCorDaFonte: config.minishopGruposCorDaFonte?.trim() || '#FFFFFF',
            // Card herda grupos e cabecalho herda botoes quando nao definidos.
            minishopCardCorDeFundo: config.minishopCardCorDeFundo?.trim() || config.minishopGruposCorDeFundo?.trim() || '#165c7d',
            minishopCardCorDaFonte: config.minishopCardCorDaFonte?.trim() || config.minishopGruposCorDaFonte?.trim() || '#FFFFFF',
            minishopCabecalhoCorDeFundo: config.minishopCabecalhoCorDeFundo?.trim() || config.minishopBotoesCorDeFundo?.trim() || '#0582c5',
            minishopCabecalhoCorDaFonte: config.minishopCabecalhoCorDaFonte?.trim() || config.minishopBotoesCorDaFonte?.trim() || '#FFFFFF',
            minishopImagemDeTitulo: config.minishopImagemDeTitulo ?? '',
            perfilInstagram: config.perfilInstagram ?? '',
            telefone: config.telefone ?? '',
            perfilFacebook: config.perfilFacebook ?? '',
            urlDoMiniShopping: config.urlDoMiniShopping ?? ''
          }, { emitEvent: false });

          // Vincula os rangos e atualiza Preview de Imagem
          this.vincularRangosPorTipo(tipoEnum);

          // Usa o campo direto do backend para construir a URL de preview
          // Evita dependência do patchValue que pode ter transformado o valor
          if (config.id && config.minishopImagemDeTitulo) {
            this.imagePreviewUrl = `${this.env.imgHost}/${config.id}/configuracao_empresa/${config.minishopImagemDeTitulo}`;
          } else {
            this.imagePreviewUrl = null;
          }
        },
        error: (err) => {
          console.error('Erro ao carregar config empresa:', err);
          const detalheErro = err?.error?.message || err?.message || JSON.stringify(err?.status);
          this.messageService.add({
            severity: 'error',
            summary: this.translate.instant('common.erro'),
            detail: `${this.translate.instant('configuracao-mine-shop.texts.erro_carregar_config')} [${detalheErro}]`,
          });
        }
      });
  }

  private vincularRangosPorTipo(tipo: string): void {
    const novoArray = tipo === 'DISTANCIA_POR_KM' ? this.faixasKM : this.faixasMetros;
    this.formCoberturaEnvio.setControl('rangos', novoArray);
  }

  /**
   * Alterna o estado de habilitar/desabilitar Mini Shopping.
   * Chamado pelo (onChange) do p-inputSwitch — o valor de
   * `habilitarMiniShopping` só é efetivado após o backend confirmar.
   */
  onToggleHabilitarMiniShopping(novoValor: boolean): void {
    // Se está habilitando e não tem URL configurada, pedir o nome da loja primeiro
    if (novoValor && !this.configuracaoEmpresaAtual?.urlDoMiniShopping?.trim()) {
      this.nomeLojaParaHabilitar = '';
      this.mostrarDialogUrl = true;
      return;
    }

    this.executarToggleHabilitar(novoValor);
  }

  /**
   * Converte o nome digitado pelo usuário (ex: "Café do João") em
   * um slug válido para compor a URL pública (ex: "cafe-do-joao").
   *
   * Regras:
   * - Remove acentos (normalização NFD).
   * - Converte para minúsculas.
   * - Substitui qualquer caractere não alfanumérico por hífen.
   * - Colapsa hífens duplicados e remove hífens das bordas.
   */
  private normalizarSlug(nome: string): string {
    if (!nome) return '';
    return nome
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase()
      .trim()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  }

  /**
   * Slug derivado em tempo real do nome digitado. Usado:
   * - Para mostrar preview "/menu/{slug}" abaixo do campo;
   * - Para validar (vazio ou fora dos limites => bloqueia submit);
   * - Para enviar ao backend em `urlDoMiniShopping` (contrato inalterado).
   */
  get slugLoja(): string {
    return this.normalizarSlug(this.nomeLojaParaHabilitar);
  }

  /**
   * Indica se o nome digitado produz um slug válido (3..60 chars).
   * O botão "Confirmar e Habilitar" fica desabilitado enquanto não válido.
   */
  get slugLojaValido(): boolean {
    const slug = this.slugLoja;
    return slug.length >= this.slugMinChars && slug.length <= this.slugMaxChars;
  }

  onConfirmarUrlEHabilitar(): void {
    // Valida o slug derivado (nome pode conter apenas caracteres especiais
    // e gerar slug vazio — nesse caso bloqueamos).
    if (!this.slugLojaValido) return;

    if (!this.configuracaoEmpresaAtual?.id) return;

    const slug = this.slugLoja;

    this.salvandoUrl = true;

    // Salva o slug via patchAparencia (mesmo campo `urlDoMiniShopping`
    // enviado antes — contrato com o backend preservado) e depois
    // habilita o minishop.
    const payload: EmpresaConfigResumidoDTO = { ...this.formAparencia.value, urlDoMiniShopping: slug };
    this.empresaConfigService.patchAparencia(this.configuracaoEmpresaAtual.id, payload).subscribe({
      next: () => {
        // Atualiza a config local e o formulário com o slug salvo
        this.configuracaoEmpresaAtual!.urlDoMiniShopping = slug;
        this.formAparencia.patchValue({ urlDoMiniShopping: slug });
        this.salvandoUrl = false;
        this.mostrarDialogUrl = false;

        // Agora habilita o minishop
        this.executarToggleHabilitar(true);
      },
      error: (err) => {
        this.salvandoUrl = false;
        const detail = err?.status === 409
          ? this.translate.instant('configuracao-mine-shop.texts.erro_url_duplicada')
          : this.translate.instant('configuracao-mine-shop.texts.erro_salvar_url');
        this.messageService.add({
          severity: 'error',
          summary: this.translate.instant('erro'),
          detail,
        });
      },
    });
  }

  /**
   * Salva as formas de pagamento aceitas no minishop.
   *
   * Usa o PATCH dedicado, como as demais seções desta tela. O PUT genérico não serve:
   * ele espera os enums no formato de entrada e a tela só tem o DTO de consulta.
   * Rollback visual em caso de erro, igual ao toggle do minishop.
   */
  salvarFormasDePagamento(): void {
    if (this.salvandoFormasDePagamento) return;

    const estadoAnterior = { ...this.formasDePagamento };
    this.salvandoFormasDePagamento = true;

    this.empresaConfigService.patchFormasDePagamentoMinishop({
      exibirFormasDePagamento: this.formasDePagamento.exibir,
      aceitaDinheiro: this.formasDePagamento.dinheiro,
      aceitaTarjeta: this.formasDePagamento.tarjeta,
      aceitaQrCode: this.formasDePagamento.qrCode,
      aceitaTransferencia: this.formasDePagamento.transferencia,
    }).subscribe({
      next: (configAtualizada: any) => {
        this.salvandoFormasDePagamento = false;
        this.configuracaoEmpresaAtual = configAtualizada ?? this.configuracaoEmpresaAtual;
        this.messageService.add({
          severity: 'success',
          summary: this.translate.instant('sucesso'),
          detail: this.translate.instant('configuracao-mine-shop.texts.formas_pagamento_salvas'),
        });
      },
      error: () => {
        this.salvandoFormasDePagamento = false;
        this.formasDePagamento = estadoAnterior;
        this.messageService.add({
          severity: 'error',
          summary: this.translate.instant('erro'),
          detail: this.translate.instant('configuracao-mine-shop.texts.erro_salvar_formas_pagamento'),
        });
      },
    });
  }

  salvarExigenciasDoPedido(): void {
    if (this.salvandoExigenciasDoPedido) return;

    // Guarda o estado para desfazer se o salvamento falhar: o toggle já mudou na tela, e
    // deixá-lo mentindo sobre o que está gravado é pior do que voltar atrás.
    const estadoAnterior = { ...this.exigenciasDoPedido };
    this.salvandoExigenciasDoPedido = true;

    this.empresaConfigService.patchExigenciasDoPedidoMinishop({
      exigirNumeroDaMesa: this.exigenciasDoPedido.numeroDaMesa,
      exigirNumeroDaComanda: this.exigenciasDoPedido.numeroDaComanda,
    }).subscribe({
      next: (configAtualizada: any) => {
        this.salvandoExigenciasDoPedido = false;
        this.configuracaoEmpresaAtual = configAtualizada ?? this.configuracaoEmpresaAtual;
        this.messageService.add({
          severity: 'success',
          summary: this.translate.instant('sucesso'),
          detail: this.translate.instant('configuracao-mine-shop.texts.exigencias_pedido_salvas'),
        });
      },
      error: () => {
        this.salvandoExigenciasDoPedido = false;
        this.exigenciasDoPedido = estadoAnterior;
        this.messageService.add({
          severity: 'error',
          summary: this.translate.instant('erro'),
          detail: this.translate.instant('configuracao-mine-shop.texts.erro_salvar_exigencias_pedido'),
        });
      },
    });
  }

  onCancelarDialogUrl(): void {
    this.mostrarDialogUrl = false;
    this.nomeLojaParaHabilitar = '';
    // O onHide do dialogo tambem dispara apos confirmar: com a habilitacao em andamento a chave
    // deve continuar ligada, entao so volta ao estado do modelo quando nada esta pendente.
    if (!this.loadingHabilitar && !this.salvandoUrl) {
      this.sincronizarChaveHabilitar();
    }
  }

  /** Faz a chave refletir o estado real (`habilitarMiniShopping`), desfazendo o clique do usuario. */
  private sincronizarChaveHabilitar(): void {
    this.chaveHabilitar?.writeValue(this.habilitarMiniShopping);
  }

  private executarToggleHabilitar(novoValor: boolean): void {
    const valorAnterior = !novoValor;
    this.loadingHabilitar = true;

    this.empresaConfigService
      .patchHabilitarMiniShopping(novoValor)
      .subscribe({
        next: (configAtualizada) => {
          // Substitui a config local pela resposta autoritativa do backend
          this.configuracaoEmpresaAtual = configAtualizada;
          this.habilitarMiniShopping = !!configAtualizada.habilitarMiniShopping;
          this.loadingHabilitar = false;

          const chaveDetail = this.habilitarMiniShopping
            ? 'configuracao-mine-shop.texts.minishop_habilitado_sucesso'
            : 'configuracao-mine-shop.texts.minishop_desabilitado_sucesso';

          this.messageService.add({
            severity: 'success',
            summary: this.translate.instant('sucesso'),
            detail: this.translate.instant(chaveDetail),
          });
        },
        error: () => {
          // Rollback visual — mantém a UI coerente com o estado do servidor
          this.habilitarMiniShopping = valorAnterior;
          this.loadingHabilitar = false;
          this.sincronizarChaveHabilitar();

          this.messageService.add({
            severity: 'error',
            summary: this.translate.instant('erro'),
            detail: this.translate.instant(
              'configuracao-mine-shop.texts.erro_atualizar_habilitar_minishop'
            ),
          });
        },
      });
  }

  openDialogAparencia(): void {
    this.estadoSalvamentoAparencia = null;
    this.mostrarConfigurarAparencia = true;
    this.registrarAutosaveAparencia();
  }

  closeAparenciaDialog(): void {
    this.mostrarConfigurarAparencia = false;
    this.estadoSalvamentoAparencia = null;
    this.destroyAparencia$.next();
    if (this.autosaveAparenciaSub) {
      this.autosaveAparenciaSub.unsubscribe();
      this.autosaveAparenciaSub = null;
    }
  }

  onSubmitAparencia(): void {
    this.executarAutosaveAparencia();
  }

  /* --- Autosave de Aparência --- */

  /**
   * Registra listeners para autosave ao abrir o painel de aparência.
   * - Campos de cor (valueChanges): debounce 600ms.
   * - Upload / remoção de banner: imediato (disparado manualmente).
   * - Reset de cores: imediato (disparado manualmente em confirmarResetCores).
   */
  private registrarAutosaveAparencia(): void {
    if (this.autosaveAparenciaSub) {
      this.autosaveAparenciaSub.unsubscribe();
    }
    this.destroyAparencia$.next();

    const autosaveSubject = new Subject<'imediato' | 'debounce'>();

    // Todos os campos do formAparencia com debounce (cores, links, etc.)
    this.formAparencia.valueChanges
      .pipe(takeUntil(this.destroyAparencia$))
      .subscribe(() => autosaveSubject.next('debounce'));

    let debounceTimer: any = null;
    this.autosaveAparenciaSub = autosaveSubject.subscribe(tipo => {
      if (tipo === 'imediato') {
        clearTimeout(debounceTimer);
        this.executarAutosaveAparencia();
      } else {
        clearTimeout(debounceTimer);
        debounceTimer = setTimeout(() => this.executarAutosaveAparencia(), 600);
      }
    });
  }

  /**
   * Executa o salvamento da aparência sem fechar o painel.
   * Atualiza o estado visual (estadoSalvamentoAparencia) para feedback discreto.
   */
  executarAutosaveAparencia(): void {
    if (this.formAparencia.invalid) return;

    if (!this.configuracaoEmpresaAtual?.id) return;

    const empresaId = this.configuracaoEmpresaAtual.id;
    const payload: EmpresaConfigResumidoDTO = { ...this.formAparencia.value };

    this.estadoSalvamentoAparencia = 'salvando';
    this.empresaConfigService.patchAparencia(empresaId, payload).subscribe({
      next: () => {
        this.estadoSalvamentoAparencia = 'guardado';
        // Nao recarregar aqui: o formulario ja reflete o estado salvo.
        // Recarregar re-aplicaria os valores do backend (patchValue) e
        // atropelaria uma cor que o usuario tenha alterado enquanto o save
        // estava em andamento (race do autosave).
      },
      error: (err) => {
        this.estadoSalvamentoAparencia = 'erro';
        const detail = err?.status === 409
          ? this.translate.instant('configuracao-mine-shop.texts.erro_url_duplicada')
          : err?.error?.message || this.translate.instant('configuracao-mine-shop.texts.erro_salvar_aparencia');
        this.messageService.add({
          severity: 'error',
          summary: this.translate.instant('common.erro'),
          detail,
        });
      }
    });
  }

  /** Cores padrao do sistema */
  private readonly coresPadrao = {
    minishopGeralCorDeFundo: '#FFFFFF',
    minishopGeralCorDaFonte: '#474747',
    minishopBotoesCorDeFundo: '#0582c5',
    minishopBotoesCorDaFonte: '#FFFFFF',
    minishopGruposCorDeFundo: '#165c7d',
    minishopGruposCorDaFonte: '#FFFFFF',
    minishopCardCorDeFundo: '#165c7d',
    minishopCardCorDaFonte: '#FFFFFF',
    minishopCabecalhoCorDeFundo: '#0582c5',
    minishopCabecalhoCorDaFonte: '#FFFFFF',
  };

  abrirConfirmarResetCores(): void {
    this.mostrarConfirmarResetCores = true;
  }

  fecharConfirmarResetCores(): void {
    this.mostrarConfirmarResetCores = false;
  }

  confirmarResetCores(): void {
    this.formAparencia.patchValue(this.coresPadrao);
    this.mostrarConfirmarResetCores = false;
    this.messageService.add({
      severity: 'info',
      summary: this.translate.instant('common.info'),
      detail: this.translate.instant('configuracao-mine-shop.texts.cores_restauradas_info'),
    });
    // Autosave imediato após restaurar cores
    if (this.mostrarConfigurarAparencia) {
      this.executarAutosaveAparencia();
    }
  }

  abrirConfirmarResetHorarios(): void {
    this.mostrarConfirmarResetHorarios = true;
  }

  fecharConfirmarResetHorarios(): void {
    this.mostrarConfirmarResetHorarios = false;
  }

  confirmarResetHorarios(): void {
    const diasForm = this.formHorario.get('dias') as FormGroup;

    this.diasSemanaOptions.forEach(dia => {
      const grupoDia = diasForm.get(dia.value) as FormGroup;
      const array = grupoDia.get('horarios') as FormArray;
      array.clear();

      const diasUteis = ['segunda', 'terca', 'quarta', 'quinta', 'sexta'];
      const diaEnum = this.mapDiaToEnum(dia.value);
      const horariosNoBanco = this.horariosPorDia[diaEnum] || [];

      if (diasUteis.includes(dia.value)) {
        // Segunda a Sexta: aberto com 2 turnos (08:00-12:00 e 13:30-18:00)
        // Reutiliza IDs existentes para que o submit use PUT (atualizar)
        // em vez de POST (criar novo), evitando duplicação de horários no banco
        const idPrimeiro = horariosNoBanco[0]?.id ?? null;
        const idSegundo = horariosNoBanco[1]?.id ?? null;

        grupoDia.get('ativo')?.setValue(true);
        array.push(this.criarHorarioComId(idPrimeiro, '08:00', '12:00'));
        array.push(this.criarHorarioComId(idSegundo, '13:30', '18:00'));

        // Remove do banco horários excedentes (além dos 2 padrão)
        horariosNoBanco.slice(2).forEach(h => {
          if (h.id) {
            this.horaDeAtendimentoService.delete(`/horaDeAtendimento/${h.id}`).subscribe();
          }
        });
      } else {
        // Sabado e Domingo: fechado
        grupoDia.get('ativo')?.setValue(false);
      }
    });

    this.mostrarConfirmarResetHorarios = false;
    this.messageService.add({
      severity: 'info',
      summary: this.translate.instant('common.info'),
      detail: this.translate.instant('configuracao-mine-shop.texts.horarios_restaurados_info'),
    });
  }

  onHexChange(event: Event, controlName: string): void {
    let value = (event.target as HTMLInputElement).value.trim();
    if (!value.startsWith('#')) value = `#${value}`;

    if (/^#([0-9A-Fa-f]{6})$/.test(value)) {
      this.formAparencia.get(controlName)?.setValue(value);
    }
  }

  // ==========================
  // IMAGE UPLOAD
  // ==========================
  async onSelectImage(event: any) {
    const file: File | undefined = event.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      this.messageService.add({
        severity: 'error',
        summary: this.translate.instant('common.erro'),
        detail: this.translate.instant('mine-shop.mensagens.selecione_apenas_imagens'),
      });
      return;
    }

    this.fileName = file.name;
    const imagem = await ImagemUtils.comprimirParaBase64(file);
    this.imagePreviewUrl = imagem.dataUrl;
    this.imageBase64 = imagem.base64;
    this.formAparencia?.get('minishopImagemDeTitulo')?.setValue(this.imageBase64);
    // Autosave imediato após upload de banner
    if (this.mostrarConfigurarAparencia) {
      this.executarAutosaveAparencia();
    }
  }

  onClearImage() {
    this.fileName = null;
    this.imageBase64 = null;
    this.imagePreviewUrl = null;
    this.formAparencia?.get('minishopImagemDeTitulo')?.setValue('');
    // Autosave imediato após remoção de banner
    if (this.mostrarConfigurarAparencia) {
      this.executarAutosaveAparencia();
    }
  }

  /* --- Seção: Cobertura e Preço --- */

  get rangos(): FormArray {
    const tipo = this.formCoberturaEnvio.get('tipoPrecoCoberturaDeEnvio')?.value;
    return tipo === 'DISTANCIA_POR_KM' ? this.faixasKM : this.faixasMetros;
  }

  openDialogPrecoECobertura(): void {
    this.mostrarConfigurarPrecoECobertura = true;
    this.estadoSalvamentoCobertura = null;

    // 1. Limpa o mapa se ele já existir para evitar conflitos de ID
    if (this.map) {
      this.map.remove();
      this.map = null;
    }

    // 2. Listener para mudanças no dropdown
    this.formCoberturaEnvio.get('tipoPrecoCoberturaDeEnvio')?.valueChanges
      .pipe(takeUntil(this.destroyCobertura$))
      .subscribe(val => {
        // Bloqueia autosaves secundários disparados pelo setControl dentro de vincularRangosPorTipo
        this.autosaveCoberturaSuprimido = true;
        // Cancela timers de debounce pendentes antes de disparar o autosave da troca de tipo
        this.autosaveCoberturaTimers.forEach(t => clearTimeout(t));
        this.autosaveCoberturaTimers = [];

        // Atualiza validador condicional do item de frete e marca a flag de sessao
        this.aplicarValidadorItemDeFrete(val);
        this.tipoMudouNestaSessao = true;

        this.vincularRangosPorTipo(val);

        if (val === 'DISTANCIA_RAIO_METROS') {
          // Container agora e persistente (via [style.display]) — se o mapa ja existe,
          // so recalculamos dimensoes (estava hidden). Se nao existe, inicializa.
          setTimeout(() => {
            if (this.map) {
              this.map.invalidateSize();
            } else {
              this.initLeafletMap();
            }
          }, 50);
        }
        // Nao destruimos o mapa ao trocar para outros tipos — so fica escondido via CSS.
        // Destruicao acontece em closePrecoECoberturaDialog() quando o sidebar fecha.

        // Se o tipo escolhido depende de coordenadas (preco fixo, raio em metros ou por km)
        // e o endereco da empresa ainda nao tem latitude/longitude, abre direto o modal de
        // edicao no mapa para que o usuario defina o ponto de origem antes de continuar.
        const tiposQueExigemCoordenadas = ['PRECO_FIXO', 'DISTANCIA_RAIO_METROS', 'DISTANCIA_POR_KM'];
        if (tiposQueExigemCoordenadas.includes(val) && !this.temCoordenadasDoEndereco()) {
          this.abrirModalEndereco();
        }

        // Reabilita autosave e executa UM único save após o ciclo de detecção
        // (garante que setControl já propagou e não há timers órfãos)
        setTimeout(() => {
          this.autosaveCoberturaSuprimido = false;
          this.executarAutosaveCobertura();
        }, 0);
      });

    // 3. Trigger inicial caso já abra em modo Raio
    const tipoInicial = this.formCoberturaEnvio.get('tipoPrecoCoberturaDeEnvio')?.value;
    if (tipoInicial === 'DISTANCIA_RAIO_METROS') {
      setTimeout(() => this.initLeafletMap(), 300);
    }

    this.registrarAutosaveCobertura();
  }

  closePrecoECoberturaDialog(): void {
    this.mostrarConfigurarPrecoECobertura = false;
    this.estadoSalvamentoCobertura = null;
    this.destroyCobertura$.next();
    this.autosaveCoberturaTimers.forEach(t => clearTimeout(t));
    this.autosaveCoberturaTimers = [];
    this.autosaveCoberturaSuprimido = false;
    this.toastErroCoberturaAtivo = false;
    // Como o container agora e persistente (style.display), destruimos o mapa apenas
    // ao fechar o sidebar — proxima abertura recria via initLeafletMap
    if (this.map) {
      this.map.remove();
      this.map = null;
    }
  }

  onSidebarShow() {
    // O tipo de cobertura precisa ser checado para saber se deve iniciar o mapa
    const tipo = this.formCoberturaEnvio.get('tipoPrecoCoberturaDeEnvio')?.value;
    if (tipo === 'DISTANCIA_RAIO_METROS') {
      setTimeout(() => {
        this.initLeafletMap();
      }, 300);
    }
  }

  adicionarRango(): void {
    const ultimoValor = this.rangos.length > 0 ? this.rangos.at(this.rangos.length - 1).get('metros')?.value : 0;
    const novoValor = ultimoValor + 500;

    this.rangos.push(this.fb.group({
      id: [null],
      metros: [novoValor, [Validators.required, Validators.min(1)]],
      preco: [0, Validators.required]
    }));
    this.updateMapCircles();
    // Autosave imediato ao adicionar faixa
    if (this.mostrarConfigurarPrecoECobertura) {
      this.executarAutosaveCobertura();
    }
  }

  removerRango(index: number): void {
    this.rangos.removeAt(index);
    this.updateMapCircles();
    // Autosave imediato ao remover faixa
    if (this.mostrarConfigurarPrecoECobertura) {
      this.executarAutosaveCobertura();
    }
  }

  private carregarFaixasExistentes(): void {
    // Suprime autosaves durante o carregamento programático das faixas para evitar loop
    this.autosaveCoberturaSuprimido = true;
    this.faixasKM.clear({ emitEvent: false });
    this.faixasMetros.clear({ emitEvent: false });

    this.distanciaDeEntregaService.get(`/distanciaDeEntrega`).subscribe((response: any) => {
      const faixas = response.content || [];
      this.faixasOriginais = [...faixas];

      faixas.forEach((f: any) => {
        const enumTipo = f.tipoPrecoCoberturaDeEnvio?.enum;

        if (enumTipo === 'PRECO_FIXO') {
          // emitEvent:false para não disparar o listener de precoFixo.valueChanges
          this.formCoberturaEnvio.get('precoFixo')?.setValue(f.valorDoEnvio, { emitEvent: false });
          return;
        }

        const grupo = this.fb.group({
          id: [f.id],
          metros: [enumTipo === 'DISTANCIA_RAIO_METROS' ? f.distanciaRaioEmMetros : f.distanciaEmKmPercorridos, Validators.required],
          preco: [f.valorDoEnvio, Validators.required]
        });

        if (enumTipo === 'DISTANCIA_RAIO_METROS') {
          // emitEvent:false para não disparar valueChanges do FormArray durante recarregamento
          this.faixasMetros.push(grupo, { emitEvent: false });
        } else if (enumTipo === 'DISTANCIA_POR_KM') {
          this.faixasKM.push(grupo, { emitEvent: false });
        }
      });

      if (this.faixasKM.length === 0) this.adicionarRangoEspecifico(this.faixasKM);
      if (this.faixasMetros.length === 0) this.adicionarRangoEspecifico(this.faixasMetros);

      const tipoAtual = this.formCoberturaEnvio.get('tipoPrecoCoberturaDeEnvio')?.value;
      this.vincularRangosPorTipo(tipoAtual);

      // Reabilita autosaves após o carregamento programático terminar
      this.autosaveCoberturaSuprimido = false;
    });
  }

  adicionarRangoEspecifico(array: FormArray): void {
    array.push(this.fb.group({
      id: [null],
      metros: [0, Validators.required],
      preco: [0, Validators.required]
    }));
  }

  iniciarEdicao() {
    this.enderecoTemporario = this.configuracaoEmpresaAtual?.endereco;
    this.isEditandoEndereco = true;
  }

  salvarEndereco() {
    if (this.configuracaoEmpresaAtual && this.enderecoTemporario) {
      this.configuracaoEmpresaAtual.endereco = this.enderecoTemporario;
    }
    this.isEditandoEndereco = false;

    // Re-inicializa o mapa com o novo endereço
    this.initLeafletMap();
  }

  // --- Modal de Endereço com Mapa ---

  /**
   * Verifica se a empresa ja tem coordenadas (latitude e longitude) salvas.
   * Considera 0/null/undefined como "nao definidas" porque a UI inicial usa esses valores
   * antes da primeira marcacao no mapa.
   */
  private temCoordenadasDoEndereco(): boolean {
    const lat = this.configuracaoEmpresaAtual?.latitude;
    const lng = this.configuracaoEmpresaAtual?.longitude;
    if (lat == null || lng == null) return false;
    return lat !== 0 || lng !== 0;
  }

  abrirModalEndereco(): void {
    // Preencher campos com dados existentes
    const endereco = this.configuracaoEmpresaAtual?.endereco || '';
    this.enderecoRua = endereco;
    this.enderecoNumero = '';
    this.enderecoComplemento = '';
    this.enderecoReferencia = '';
    this.erroGeolocalizacao = '';
    this.mostrarModalEndereco = true;
    // O mapa é inicializado via (onShow) do p-dialog, que dispara após a animação de entrada terminar
  }

  /** Chamado pelo evento (onShow) do p-dialog — garante que o container #map-endereco-modal
   *  já está no DOM com dimensões reais antes de o Leaflet tentar inicializar. */
  onModalEnderecoShow(): void {
    this.inicializarMapaEndereco();
  }

  fecharModalEndereco(): void {
    this.mostrarModalEndereco = false;
    this.destruirMapaEndereco();
  }

  onUsarMinhaLocalizacao(): void {
    if (!navigator.geolocation) {
      this.erroGeolocalizacao = 'configuracao-mine-shop.texts.endereco_erro_geolocalizacao';
      return;
    }

    this.carregandoLocalizacao = true;
    this.erroGeolocalizacao = '';

    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const lat = pos.coords.latitude;
        const lng = pos.coords.longitude;

        if (this.configuracaoEmpresaAtual) {
          this.configuracaoEmpresaAtual.latitude = lat;
          this.configuracaoEmpresaAtual.longitude = lng;
        }
        this.center = [lat, lng];

        this.centralizarMapaEndereco(lat, lng);
        this.geocodificarReverso(lat, lng);
        this.carregandoLocalizacao = false;
      },
      (err) => {
        if (err.code === err.PERMISSION_DENIED) {
          this.erroGeolocalizacao = 'configuracao-mine-shop.texts.endereco_erro_geolocalizacao_negada';
        } else {
          this.erroGeolocalizacao = 'configuracao-mine-shop.texts.endereco_erro_geolocalizacao';
        }
        this.carregandoLocalizacao = false;
      },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  }

  confirmarEndereco(): void {
    // Montar endereço completo a partir dos campos
    let enderecoCompleto = this.enderecoRua;
    if (this.enderecoNumero) {
      enderecoCompleto += ', ' + this.enderecoNumero;
    }
    if (this.enderecoComplemento) {
      enderecoCompleto += ' - ' + this.enderecoComplemento;
    }
    if (this.enderecoReferencia) {
      enderecoCompleto += ' (' + this.enderecoReferencia + ')';
    }

    if (this.configuracaoEmpresaAtual) {
      this.configuracaoEmpresaAtual.endereco = enderecoCompleto;
    }

    this.isEditandoEndereco = false;
    this.fecharModalEndereco();

    // Re-inicializa o mapa de cobertura com o novo endereço
    this.initLeafletMap();

    // Autosave imediato após confirmação de endereço
    if (this.mostrarConfigurarPrecoECobertura) {
      this.executarAutosaveCobertura();
    }
  }

  get enderecoFormValido(): boolean {
    return !!this.enderecoRua?.trim();
  }

  private async inicializarMapaEndereco(): Promise<void> {
    if (!isPlatformBrowser(this.platformId)) return;

    const mapContainer = document.getElementById('map-endereco-modal');
    if (!mapContainer) return;

    if (this.mapEndereco) {
      this.mapEndereco.remove();
      this.mapEndereco = null;
    }

    if (!this.L) {
      // import dinamico retorna { default: {...} } no bundle do Angular — usar .default
      const leafletModule: any = await import('leaflet');
      this.L = leafletModule.default ?? leafletModule;
      this.fixLeafletIcons();
    }

    // Determinar centro: usar coordenadas existentes ou geocodificar endereço
    let centro: [number, number] = [...this.center];
    if (this.configuracaoEmpresaAtual?.latitude && this.configuracaoEmpresaAtual?.longitude) {
      centro = [this.configuracaoEmpresaAtual.latitude, this.configuracaoEmpresaAtual.longitude];
    }

    this.mapEndereco = this.L.map('map-endereco-modal', { zoomControl: true }).setView(centro, 15);
    this.L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '&copy; OpenStreetMap'
    }).addTo(this.mapEndereco);

    this.markerEndereco = this.L.marker(centro, { draggable: true, autoPan: true }).addTo(this.mapEndereco);

    // Evento: fim do arrasto do marcador
    this.markerEndereco.on('dragend', () => {
      const pos = this.markerEndereco.getLatLng();
      if (this.configuracaoEmpresaAtual) {
        this.configuracaoEmpresaAtual.latitude = pos.lat;
        this.configuracaoEmpresaAtual.longitude = pos.lng;
      }
      this.center = [pos.lat, pos.lng];
      this.geocodificarReversoComDebounce(pos.lat, pos.lng);
    });

    // Evento: clique no mapa para mover marcador
    this.mapEndereco.on('click', (e: any) => {
      const lat = e.latlng.lat;
      const lng = e.latlng.lng;
      if (this.configuracaoEmpresaAtual) {
        this.configuracaoEmpresaAtual.latitude = lat;
        this.configuracaoEmpresaAtual.longitude = lng;
      }
      this.center = [lat, lng];
      this.markerEndereco.setLatLng([lat, lng]);
      this.geocodificarReversoComDebounce(lat, lng);
    });

    setTimeout(() => {
      if (this.mapEndereco) this.mapEndereco.invalidateSize();
    }, 250);

    // Se não tem coordenadas salvas, solicitar geolocalização automaticamente
    if (!this.configuracaoEmpresaAtual?.latitude && !this.configuracaoEmpresaAtual?.longitude) {
      this.onUsarMinhaLocalizacao();
    }
  }

  private centralizarMapaEndereco(lat: number, lng: number): void {
    if (this.mapEndereco) {
      this.mapEndereco.setView([lat, lng], 16);
    }
    if (this.markerEndereco) {
      this.markerEndereco.setLatLng([lat, lng]);
    }
  }

  private geocodificarReversoComDebounce(lat: number, lng: number): void {
    clearTimeout(this.geocodingTimeout);
    this.geocodingTimeout = setTimeout(() => {
      this.geocodificarReverso(lat, lng);
    }, 500);
  }

  private async geocodificarReverso(lat: number, lng: number): Promise<void> {
    try {
      const url = `https://nominatim.openstreetmap.org/reverse?lat=${lat}&lon=${lng}&format=json&addressdetails=1`;
      const resp = await fetch(url, {
        headers: { 'Accept-Language': 'es' }
      });
      const data = await resp.json();
      if (data.address) {
        this.enderecoRua = data.address.road || data.display_name || '';
        if (data.address.house_number) {
          this.enderecoNumero = data.address.house_number;
        }
      }
    } catch {
      // Silencioso: usuario pode preencher manualmente
    }
  }

  private destruirMapaEndereco(): void {
    clearTimeout(this.geocodingTimeout);
    if (this.mapEndereco) {
      this.mapEndereco.remove();
      this.mapEndereco = null;
    }
    this.markerEndereco = null;
  }

  onSubmitCoberturaEnvio(): void {
    this.executarAutosaveCobertura();
  }

  /* --- Autosave de Cobertura --- */

  /**
   * Registra listeners para autosave ao abrir o painel de cobertura.
   * - Mudança de tipo: imediato (tratado no valueChanges do dropdown em openDialogPrecoECobertura).
   * - Campos numéricos (precoFixo, rangos metros/preco): debounce 600ms.
   * - Adicionar/remover rangos: imediato (disparado manualmente em adicionarRango/removerRango).
   */
  private registrarAutosaveCobertura(): void {
    this.destroyCobertura$.next();

    // precoFixo: debounceTime cancela emissoes anteriores automaticamente
    // (usar setTimeout por keystroke criava multiplos timers sem cancelar os antigos)
    this.formCoberturaEnvio.get('precoFixo')?.valueChanges
      .pipe(debounceTime(800), takeUntil(this.destroyCobertura$))
      .subscribe(() => this.executarAutosaveCobertura());

    this.faixasKM.valueChanges
      .pipe(debounceTime(800), takeUntil(this.destroyCobertura$))
      .subscribe(() => this.executarAutosaveCobertura());

    this.faixasMetros.valueChanges
      .pipe(debounceTime(800), takeUntil(this.destroyCobertura$))
      .subscribe(() => this.executarAutosaveCobertura());
  }

  /**
   * Executa o salvamento de cobertura/preço sem fechar o painel.
   * Atualiza o estado visual (estadoSalvamentoCobertura) para feedback discreto.
   */
  executarAutosaveCobertura(): void {
    if (!this.configuracaoEmpresaAtual?.id) return;
    // Suprimido durante troca de tipo para evitar múltiplos saves simultâneos
    if (this.autosaveCoberturaSuprimido) return;

    const tipo = this.formCoberturaEnvio.get('tipoPrecoCoberturaDeEnvio')?.value;

    // Gate: quando o tipo exige frete, o item de frete e obrigatorio.
    // Bloqueia o autosave e exibe feedback ao usuario.
    const ctrlItem = this.formCoberturaEnvio.get('itemDeFreteId');
    if (tipo && tipo !== 'SEM_PRECO' && ctrlItem && ctrlItem.invalid) {
      ctrlItem.markAsTouched();
      this.estadoSalvamentoCobertura = null;
      if (!this.toastErroCoberturaAtivo) {
        this.toastErroCoberturaAtivo = true;
        this.messageService.add({
          severity: 'warn',
          summary: this.translate.instant('common.atencao'),
          detail: this.translate.instant('configuracao-mine-shop.texts.item_de_frete_obrigatorio'),
          life: 4000,
        });
        setTimeout(() => { this.toastErroCoberturaAtivo = false; }, 4500);
      }
      return;
    }

    this.estadoSalvamentoCobertura = 'salvando';

    // Salva rangos e configuração de cobertura em paralelo
    this.salvarRangos(tipo);

    // Monta payload respeitando semantica MERGE (ver DESIGN_FE D6).
    // itemDeFreteId so entra quando:
    //   (a) o controle esta dirty, OU
    //   (b) o usuario trocou o tipo nesta sessao e o tipo atual != SEM_PRECO.
    const payload: EmpresaConfigPrecoCoberturaDeEnvioDTO = {
      tipoPrecoCoberturaDeEnvio: tipo,
      endereco: this.configuracaoEmpresaAtual?.endereco || '',
      latitude: this.configuracaoEmpresaAtual?.latitude,
      longitude: this.configuracaoEmpresaAtual?.longitude,
    };
    const incluirItemDeFrete = !!ctrlItem?.dirty
      || (this.tipoMudouNestaSessao && tipo !== 'SEM_PRECO');
    if (incluirItemDeFrete) {
      payload.itemDeFreteId = (ctrlItem?.value ?? null) as number | null;
    }

    this.empresaConfigService.patchPrecoCoberturaDeEnvio(payload).subscribe({
      next: () => {
        this.estadoSalvamentoCobertura = 'guardado';
        // Apos salvar com sucesso, considera que a sessao foi "persistida" —
        // reseta a flag para que proximos autosaves voltem a MERGE puro.
        this.tipoMudouNestaSessao = false;
        // Nao recarrega do backend apos salvar: recriaria os FormGroups das
        // faixas, interrompendo a digitacao do usuario e disparando novos
        // valueChanges em cadeia. IDs recem-criados sao irrelevantes para o
        // proximo autosave (patchPrecoCoberturaDeEnvio nao depende deles).
      },
      error: (err) => {
        this.estadoSalvamentoCobertura = 'erro';
        const code = this.extrairCodigoErroBackend(err);

        // Erros 422 conhecidos do backend (item-frete-minishop)
        if (code === 'item_de_frete_obrigatorio') {
          const ctrl = this.formCoberturaEnvio.get('itemDeFreteId');
          ctrl?.setErrors({ backend: true });
          ctrl?.markAsTouched();
          this.mostrarToastErroCobertura(
            'warn',
            this.translate.instant('configuracao-mine-shop.texts.erro_item_de_frete_obrigatorio'),
          );
          return;
        }
        if (code === 'item_de_frete_deve_ser_servico') {
          // Limpa os dois controles para forcar nova escolha
          this.formCoberturaEnvio.patchValue({
            itemDeFreteSelecionado: null,
            itemDeFreteId: null,
          });
          const ctrl = this.formCoberturaEnvio.get('itemDeFreteId');
          ctrl?.markAsDirty();
          ctrl?.markAsTouched();
          ctrl?.updateValueAndValidity();
          this.mostrarToastErroCobertura(
            'warn',
            this.translate.instant('configuracao-mine-shop.texts.erro_item_de_frete_deve_ser_servico'),
          );
          return;
        }

        // Fallback: erro generico existente (preserva comportamento anterior)
        const detalheErro = err?.error?.message || err?.message || JSON.stringify(err?.status);
        this.mostrarToastErroCobertura(
          'error',
          `${this.translate.instant('configuracao-mine-shop.texts.erro_salvar_cobertura') || this.translate.instant('configuracao-mine-shop.texts.erro_config_nao_carregada')} [${detalheErro}]`,
        );
      }
    });
  }

  /** Exibe um toast de erro de cobertura respeitando o gate anti-empilhamento. */
  private mostrarToastErroCobertura(severity: 'warn' | 'error', detail: string): void {
    if (this.toastErroCoberturaAtivo) return;
    this.toastErroCoberturaAtivo = true;
    this.messageService.add({
      severity,
      summary: severity === 'warn'
        ? this.translate.instant('common.atencao')
        : this.translate.instant('common.erro'),
      detail,
      life: 4000,
    });
    setTimeout(() => { this.toastErroCoberturaAtivo = false; }, 4500);
  }

  salvarRangos(tipo: any): void {
    const rangosForm = this.formCoberturaEnvio.value.rangos;
    const empresaId = this.configuracaoEmpresaAtual?.id;

    // 1. Lógica para PRECO_FIXO (Garante apenas 1 registro)
    if (tipo === 'PRECO_FIXO') {
      const valorFixo = this.formCoberturaEnvio.get('precoFixo')?.value;
      // Busca se já existe algum registro desse tipo no banco
      const existente = this.faixasOriginais.find(f => f.tipoPrecoCoberturaDeEnvio?.enum === 'PRECO_FIXO');

      const payload = {
        distanciaRaioEmMetros: 0,
        distanciaEmKmPercorridos: 0,
        valorDoEnvio: valorFixo,
        tipoPrecoCoberturaDeEnvio: tipo,
        empresa_id: empresaId
      };

      if (existente) {
        this.distanciaDeEntregaService.put(`/distanciaDeEntrega/${existente.id}`, payload).subscribe();
      } else {
        this.distanciaDeEntregaService.post('/distanciaDeEntrega', payload).subscribe();
      }
      return;
    }

    // 2. Lógica para DISTANCIA_POR_KM (Garante apenas 1)
    if (tipo === 'DISTANCIA_POR_KM') {
      // Pegamos apenas o primeiro item do formArray (índice 0)
      const r = rangosForm[0];
      const existente = this.faixasOriginais.find(f => f.tipoPrecoCoberturaDeEnvio?.enum === 'DISTANCIA_POR_KM');

      const payload = {
        distanciaRaioEmMetros: 0,
        distanciaEmKmPercorridos: r.metros,
        valorDoEnvio: r.preco,
        tipoPrecoCoberturaDeEnvio: tipo,
        empresa_id: empresaId
      };

      if (existente) {
        this.distanciaDeEntregaService.put(`/distanciaDeEntrega/${existente.id}`, payload).subscribe();
      } else {
        this.distanciaDeEntregaService.post('/distanciaDeEntrega', payload).subscribe();
      }
      return;
    }

    // 3. Lógica para DISTANCIA_RAIO_METROS (Mantém múltiplas faixas)
    if (tipo === 'DISTANCIA_RAIO_METROS') {
      // Remove do banco faixas que não estão mais no formulário
      const idsNoForm = rangosForm.map((rf: any) => rf.id).filter((id: any) => id !== null);
      const removidas = this.faixasOriginais.filter(f =>
        f.tipoPrecoCoberturaDeEnvio?.enum === 'DISTANCIA_RAIO_METROS' && !idsNoForm.includes(f.id)
      );

      removidas.forEach(f => {
        this.distanciaDeEntregaService.delete(`/distanciaDeEntrega/${f.id}`).subscribe();
      });

      // Salva ou Atualiza as faixas atuais
      rangosForm.forEach((r: any) => {
        const payload = {
          distanciaRaioEmMetros: r.metros,
          distanciaEmKmPercorridos: 0,
          valorDoEnvio: r.preco,
          tipoPrecoCoberturaDeEnvio: tipo,
          empresa_id: empresaId
        };

        if (r.id) {
          this.distanciaDeEntregaService.put(`/distanciaDeEntrega/${r.id}`, payload).subscribe();
        } else {
          this.distanciaDeEntregaService.post('/distanciaDeEntrega', payload).subscribe();
        }
      });
    }
  }

  private finalizarSalvamento(): void {
    this.closePrecoECoberturaDialog();
    this.carregarConfiguracaoEmpresa();
  }

  /* --- Seção: Mapa e Geocodificação --- */

  private isInitializingLeafletMap = false;

  async initLeafletMap(): Promise<void> {
    if (!isPlatformBrowser(this.platformId)) return;

    // Serializa chamadas concorrentes. O metodo tem awaits (import leaflet, geocoding)
    // durante os quais valueChanges pode re-disparar — sem isso, duas inicializacoes
    // rodam em paralelo e a segunda estoura "Map container is already initialized"
    if (this.isInitializingLeafletMap) return;
    this.isInitializingLeafletMap = true;

    try {
      // Retry: aguarda o div #map-leaflet aparecer no DOM (apos *ngIf do Angular renderizar)
      let mapContainer: any = null;
      for (let tries = 0; tries < 20; tries++) {
        mapContainer = document.getElementById('map-leaflet');
        if (mapContainer) break;
        await new Promise(r => setTimeout(r, 50));
      }
      if (!mapContainer) return;

      // Destroi mapa anterior
      if (this.map) {
        this.map.remove();
        this.map = null;
      }

      // 2. Carrega a biblioteca se necessário
      if (!this.L) {
        // import dinamico retorna { default: {...} } no bundle do Angular — usar .default
        const leafletModule: any = await import('leaflet');
        this.L = leafletModule.default ?? leafletModule;
        this.fixLeafletIcons();
      }

      // 3. Determina o centro (lógica de endereço vs coordenadas do banco)
      const enderecoParaBuscar = this.configuracaoEmpresaAtual?.endereco;
      if (enderecoParaBuscar && !this.configuracaoEmpresaAtual?.latitude) {
        const novasCoordenadas = await this.buscarCoordenadasPorEndereco(enderecoParaBuscar);
        if (novasCoordenadas) this.center = novasCoordenadas;
      } else if (this.configuracaoEmpresaAtual?.latitude && this.configuracaoEmpresaAtual?.longitude) {
        this.center = [this.configuracaoEmpresaAtual.latitude, this.configuracaoEmpresaAtual.longitude];
      }

      // Re-obter o container APOS os awaits (Angular pode ter recriado via *ngIf neste meio tempo)
      // e limpar _leaflet_id de qualquer container orfao (antigo ainda no DOM durante transicao)
      mapContainer = document.getElementById('map-leaflet');
      if (!mapContainer) return;
      document.querySelectorAll('[id="map-leaflet"]').forEach(el => {
        delete (el as any)._leaflet_id;
      });

      // 4. Inicializa o novo Mapa — passa ELEMENTO DOM (nao string) para garantir que o
      // Leaflet use o container que acabamos de limpar, nao outro via getElementById interno
      this.map = this.L.map(mapContainer, { zoomControl: false }).setView(this.center, 13);
      this.L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        attribution: '© OpenStreetMap contributors'
      }).addTo(this.map);

      // 5. Marcador interativo
      this.mainMarker = this.L.marker(this.center, {
        draggable: true,
        autoPan: true
      }).addTo(this.map);

      // 6. Eventos
      this.map.on('click', (e: any) => {
        this.atualizarPontoCentral(e.latlng.lat, e.latlng.lng);
      });
      this.mainMarker.on('dragend', () => {
        const position = this.mainMarker.getLatLng();
        this.atualizarPontoCentral(position.lat, position.lng);
      });

      // 7. Grupo de círculos
      this.circleGroup = this.L.featureGroup().addTo(this.map);

      // 8. Ajuste final de renderização
      setTimeout(() => {
        if (this.map) {
          this.map.invalidateSize();
          this.updateMapCircles();
        }
      }, 250);
    } finally {
      // Sempre libera a flag (mesmo em early return ou exception)
      this.isInitializingLeafletMap = false;
    }
  }

  updateMapCircles(): void {
    if (!this.map || !this.L || !this.circleGroup) return;

    this.circleGroup.clearLayers();
    const cores = [...this.coresFaixas];

    this.rangos.controls.forEach((control, i) => {
      const val = control.value;
      if (val.metros > 0) {
        this.L.circle(this.center, {
          radius: val.metros,
          color: cores[i % cores.length],
          fillOpacity: 0.15,
          weight: 2
        }).addTo(this.circleGroup);
      }
    });

    if (this.circleGroup.getLayers().length > 0) {
      this.map.fitBounds(this.circleGroup.getBounds(), { padding: [20, 20] });
    }
  }

  private async buscarCoordenadasPorEndereco(endereco: string): Promise<[number, number] | null> {
    if (!isPlatformBrowser(this.platformId)) return null;

    const cidadeEstadoPais = this.configuracaoEmpresaAtual?.cidade?.nome
      ? `${this.configuracaoEmpresaAtual?.cidade?.nome}, ${this.configuracaoEmpresaAtual?.cidade.estado?.nome}, ${this.configuracaoEmpresaAtual?.cidade.estado.pais?.nome}`
      : '';

    const query = encodeURIComponent(endereco + ', ' + cidadeEstadoPais);
    const url = `https://nominatim.openstreetmap.org/search?format=json&q=${query}&limit=1`;

    try {
      const response = await fetch(url, { headers: { 'Accept-Language': 'pt-BR' } });
      const data = await response.json();
      if (data && data.length > 0) {
        return [parseFloat(data[0].lat), parseFloat(data[0].lon)];
      }
      return null;
    } catch (error) {
      console.error('Erro na geocodificação:', error);
      return null;
    }
  }

  /* --- Helpers --- */

  /**
   * Validator que garante que o valor de hora está completamente preenchido no formato HH:mm (sem
   * caracteres residuais do slot da máscara, como "H", "M" etc.) E que representa uma hora válida
   * (HH entre 00-23, MM entre 00-59). A validação semântica evita que estados intermediários do
   * p-inputMask (ex.: "00:70" ao digitar "00:00" sobre "07:00") cheguem ao autosave e quebrem
   * no backend.
   */
  static horaCompletaValidator(control: AbstractControl): ValidationErrors | null {
    const valor: string = control.value ?? '';
    if (!valor) {
      return null;
    }
    const match = /^(\d{2}):(\d{2})$/.exec(valor);
    if (!match) {
      return { horaIncompleta: true };
    }
    const horas = Number(match[1]);
    const minutos = Number(match[2]);
    if (horas > 23 || minutos > 59) {
      return { horaIncompleta: true };
    }
    return null;
  }

  private criarHorarioComId(id: number | null, abertura: string, fechamento: string): FormGroup {
    return this.fb.group({
      id: [id],
      abertura: [abertura || '', [Validators.required, ConfiguracaoMineShopComponent.horaCompletaValidator]],
      fechamento: [fechamento || '', [Validators.required, ConfiguracaoMineShopComponent.horaCompletaValidator]]
    });
  }

  mapDiaToEnum(dia: string): 'MONDAY' | 'TUESDAY' | 'WEDNESDAY' | 'THURSDAY' | 'FRIDAY' | 'SATURDAY' | 'SUNDAY' {
    const map = {
      segunda: 'MONDAY', terca: 'TUESDAY', quarta: 'WEDNESDAY',
      quinta: 'THURSDAY', sexta: 'FRIDAY', sabado: 'SATURDAY', domingo: 'SUNDAY'
    } as const;
    return map[dia as keyof typeof map];
  }

  formatHora(hora: string): string {
    return hora?.substring(0, 5);
  }

  private dateToLocalTime(value: any): string {
    if (!value) return '';
    if (typeof value === 'string') return value.length === 5 ? `${value}:00` : value;
    if (value instanceof Date) {
      const h = value.getHours().toString().padStart(2, '0');
      const m = value.getMinutes().toString().padStart(2, '0');
      return `${h}:${m}:00`;
    }
    return '';
  }

  private fixLeafletIcons(): void {
    corrigirIconesLeaflet(this.L);
  }

  // Método para centralizar e atualizar coordenadas
  private atualizarPontoCentral(lat: number, lng: number): void {
    this.center = [lat, lng];

    if (this.configuracaoEmpresaAtual) {
      this.configuracaoEmpresaAtual.latitude = lat;
      this.configuracaoEmpresaAtual.longitude = lng;
    }

    if (this.mainMarker) {
      this.mainMarker.setLatLng(this.center);
    }

    // Importante: Redesenhar os círculos de raio ao redor do novo centro
    this.updateMapCircles();
  }
}
